import React, { MouseEventHandler, useState } from 'react';
import cn from 'classnames';
import throttle from 'lodash.throttle';
import appendQuery from 'append-query';

import { isTouchDevice } from 'helpers/ClientUtils';
import COLOR_SWATCH_MAP from 'constants/colorSwatchMap';
import { Product, ProductWithStyles } from 'constants/searchTypes';
import { HeartsData } from 'types/product';
import Scroller from 'components/common/Scroller';
import ProductCard from 'components/common/ProductCard';
import { track } from 'apis/amethyst';
import { evSwatchInteract } from 'events/search';
import { LazyImage } from 'components/common/Image';
import MsaImageParams from 'types/msaImageParams';
import Tooltip from 'components/common/Tooltip';
import ProductUtils, { getImageIdFromImageMap, imageMapTypes } from 'helpers/ProductUtils';
import { constructMSAImageUrl, pluralize } from 'helpers/index';
import MartyLink from 'components/common/MartyLink';
import useMartyContext from 'hooks/useMartyContext';
import { ProductWithRelations } from 'types/calypso';
import { BadgeData } from 'types/badges';
import { useIsMinInnerWidth } from 'hooks/useWindowSize';

import css from 'styles/components/search/colorswatches.scss';

// this matches the breakpoint at which the product cards on search to maintain the minimum card width needed for 4 swatches to not overflow.
const MIN_BREAKPOINT_TO_SHOW_4_SWATCHES = 1042;

interface ColorSwatchProps {
  relatedStyle: Product;
  isActive: boolean;
  makeSwatchClickHandler: (swatch: Product, interactedSwatchIndex: number) => MouseEventHandler;
  swatchIndexReference: number;
  testId: (id: string) => string;
  useMouseEnterEvent?: boolean;
  hasTooltip?: boolean;
}

interface SwatchesProps extends ProductWithStyles, Pick<ColorSwatchProps, 'useMouseEnterEvent' | 'makeSwatchClickHandler'> {
  isHydraSwatchGrouping?: boolean;

  // primarily to switch it on for search, and off for PDP until desired.
  allowSwatchGrouping?: boolean;
}

const placeholderSwatch = <span />;

const ColorSwatch = ({
  relatedStyle,
  isActive,
  useMouseEnterEvent,
  makeSwatchClickHandler,
  swatchIndexReference,
  hasTooltip,
  testId
}: ColorSwatchProps) => {
  const { styleColor, swatchUrl, color, imageMap } = relatedStyle;
  const colorName = COLOR_SWATCH_MAP[color?.toLowerCase()] || 'unknown';

  let webpSrc;

  const imgId = getImageIdFromImageMap(imageMap, imageMapTypes.SWCH);

  const imageUrl = constructMSAImageUrl(imgId, {
    width: 73,
    height: 73,
    autoCrop: true,
    useWebp: true
  });

  if (swatchUrl) {
    webpSrc = ProductUtils.replaceJpgToWebpSwatchUrl(swatchUrl);
  }

  return (
    <button
      className={css.swatchButton}
      title={!hasTooltip ? styleColor : undefined}
      aria-label={`${isActive ? 'Selected color' : 'Color'}, ${styleColor}, button`}
      type="button"
      onMouseEnter={useMouseEnterEvent ? makeSwatchClickHandler(relatedStyle, swatchIndexReference) : () => {}}
      onClick={makeSwatchClickHandler(relatedStyle, swatchIndexReference)}
      data-test-id={testId('colorSwatchButton')}
    >
      {swatchUrl || imageUrl ? (
        <LazyImage
          alt={color}
          className={cn({ [css.active]: isActive })}
          height={34}
          placeholder={placeholderSwatch}
          src={swatchUrl || imageUrl}
          srcSet={swatchUrl || imageUrl}
          webp={webpSrc ? { srcSet: webpSrc, src: webpSrc } : undefined}
          width={34}
        />
      ) : (
        <span
          className={cn({
            [css.unknown]: colorName === 'unknown',
            [css.active]: isActive
          })}
          style={{ background: colorName }}
        />
      )}
    </button>
  );
};

export const ColorSwatches = ({
  relatedStyles,
  makeSwatchClickHandler,
  colorId: mainColorId,
  styleId: mainStyleId,
  useMouseEnterEvent = true,
  allowSwatchGrouping = false,
  isHydraSwatchGrouping,
  productSeoUrl,
  onClick
}: SwatchesProps) => {
  const shouldShowFourSwatches = useIsMinInnerWidth(MIN_BREAKPOINT_TO_SHOW_4_SWATCHES, 100);
  const { testId } = useMartyContext();

  if (!relatedStyles?.length) {
    return <div className={css.wrapper}></div>;
  }

  if (allowSwatchGrouping) {
    const { visibleFabricSwatches, groupedSwatches, hydraSwatches } = ProductUtils.groupVisibleSwatches(
      relatedStyles,
      shouldShowFourSwatches,
      isHydraSwatchGrouping
    );
    let groupedSwatchesProductLink = productSeoUrl;
    // set the first productSeoUrl to the group swatch link
    if (groupedSwatches.length > 0 && groupedSwatches[0]) {
      groupedSwatchesProductLink = groupedSwatches[0].productSeoUrl;
    }

    const groupSwatchLength = ProductUtils.getGroupedSwatchLength(groupedSwatches, visibleFabricSwatches, hydraSwatches);

    return (
      <section className={cn(css.wrapper, css.isSwatchGrouping)}>
        <ul className={css.contentClass} data-test-id={testId('colorSwatch')}>
          {visibleFabricSwatches.map((relatedStyle, i) => (
            <li key={relatedStyle.colorId}>
              <Tooltip contentClassName={css.tooltipContent} tooltipClassName={css.tooltip} direction="bottom" content={relatedStyle.styleColor}>
                <ColorSwatch
                  relatedStyle={relatedStyle}
                  useMouseEnterEvent={useMouseEnterEvent}
                  makeSwatchClickHandler={makeSwatchClickHandler}
                  swatchIndexReference={i}
                  isActive={mainColorId === relatedStyle.colorId && mainStyleId === relatedStyle.styleId}
                  hasTooltip={true}
                  testId={testId}
                />
              </Tooltip>
            </li>
          ))}
          {groupedSwatches.length > 0 && (
            <li key="grouped-swatches-button">
              <Tooltip
                contentClassName={css.tooltipContent}
                tooltipClassName={css.tooltip}
                direction="bottom"
                content={`${groupSwatchLength} ${pluralize('more color', groupSwatchLength)}`}
              >
                <MartyLink
                  to={groupedSwatchesProductLink}
                  onClick={onClick}
                  aria-label={`Link, ${groupSwatchLength} ${pluralize('more color', groupSwatchLength)}`}
                  className={css.swatchButton}
                >
                  {groupSwatchLength !== undefined && (
                    <span
                      className={cn(css.groupedSwatches, {
                        [css.active]: !!groupedSwatches.find(s => mainColorId === s.colorId && mainStyleId === s.styleId)
                      })}
                    >{`+${groupSwatchLength}`}</span>
                  )}
                </MartyLink>
              </Tooltip>
            </li>
          )}
          {hydraSwatches.length > 0 && !groupedSwatches.length && !visibleFabricSwatches.length && (
            <li key="hydra-grouped-swatches-button">
              <MartyLink
                className={css.underline}
                to={groupedSwatchesProductLink}
                onClick={onClick}
                aria-label={`Link, ${hydraSwatches.length} ${pluralize('more color', hydraSwatches.length)}`}
              >
                <span
                  className={cn(css.groupedSwatches, {
                    [css.active]: !!hydraSwatches.find(s => mainColorId === s.colorId && mainStyleId === s.styleId)
                  })}
                >{`+${hydraSwatches.length} ${pluralize('color')}/${pluralize('pattern')}`}</span>
              </MartyLink>
            </li>
          )}
        </ul>
      </section>
    );
  }

  return (
    <Scroller>
      {({ contentRef, leftArrowClick, rightArrowClick, arrowsVisible, scrollLeftDisabled, scrollRightDisabled }) => (
        <section className={cn(css.wrapper, { [css.hasArrows]: arrowsVisible })}>
          <button
            type="button"
            className={css.leftArrow}
            disabled={scrollLeftDisabled}
            data-test-id={testId('scrollLeftButton')}
            onClick={leftArrowClick}
          >
            Scroll Left
          </button>
          <ul ref={contentRef} className={css.contentClass} data-test-id={testId('colorSwatch')}>
            {relatedStyles.map((relatedStyle, i) => (
              <li key={relatedStyle.colorId}>
                <ColorSwatch
                  relatedStyle={relatedStyle}
                  useMouseEnterEvent={useMouseEnterEvent}
                  makeSwatchClickHandler={makeSwatchClickHandler}
                  swatchIndexReference={i}
                  isActive={mainColorId === relatedStyle.colorId && mainStyleId === relatedStyle.styleId}
                  testId={testId}
                />
              </li>
            ))}
          </ul>
          <button
            type="button"
            className={css.rightArrow}
            disabled={scrollRightDisabled}
            data-test-id={testId('scrollRightButton')}
            onClick={rightArrowClick}
          >
            Scroll Right
          </button>
        </section>
      )}
    </Scroller>
  );
};

interface BadgeProps {
  imageBadgeClassName?: string;
  absoluteBadgeClassName?: string;
  badges?: BadgeData[];
}

interface BaseWrapperProps extends BadgeProps {
  testId: string;
  animationTimerIndex?: number;
  heartsInfo: HeartsData;
  msaImageParams?: MsaImageParams;
  onProductMediaHovered: <P>(hoveredProduct: P, mainStyleId: string) => void;
  className?: string;
  imageClassName?: string;
  relativeBadgeClassName?: string;
  absoluteBadgeClassName?: string;
  // landing pages and slots specific
  eventLabel?: string;
  isCrossSiteSearch?: boolean;
  siteName?: string;
  onComponentClick?: (evt: React.MouseEvent<HTMLAnchorElement>, product: Product) => void;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => any;
  retainQueryString?: URLSearchParams;
}

interface WrapperPropsForProduct extends BaseWrapperProps, ProductWithStyles, Pick<SwatchesProps, 'allowSwatchGrouping'> {}

interface WrapperPropsForRecos extends BaseWrapperProps, ProductWithRelations, Pick<SwatchesProps, 'allowSwatchGrouping'> {}

type WrapperProps = WrapperPropsForProduct | WrapperPropsForRecos;

export const getQueryString = (url: string) => {
  // baseurl doesnt matter
  const u = new URL(`https://www.zappos.com${url}`);
  return u.searchParams;
};

const ColorSwatchWrapper = ({
  allowSwatchGrouping = false,
  testId,
  animationTimerIndex,
  heartsInfo,
  onProductMediaHovered,
  className,
  relativeBadgeClassName,
  absoluteBadgeClassName,
  eventLabel,
  isCrossSiteSearch,
  siteName,
  onComponentClick,
  onClick,
  retainQueryString,
  ...mainProduct
}: WrapperProps) => {
  const {
    marketplace: {
      search: { enableColorSwatches }
    }
  } = useMartyContext();
  const [currentProduct, setCurrentProduct] = useState<Product | ProductWithRelations>(mainProduct);

  // the original product can be loaded w/ params important for reco tracking. On swatch changes, these params are lost as they dont exist on the swatch url.
  // we need to capture the original params and append them later if they exist.
  const [originalQueryString] = useState<URLSearchParams>(retainQueryString || getQueryString(mainProduct.productUrl));
  const { styleId } = mainProduct;

  const makeSwatchClickHandler = (swatch: Product | ProductWithRelations, interactedSwatchIndex: number) =>
    throttle(e => {
      const touchDevice = isTouchDevice();
      const newProduct = { ...mainProduct, ...swatch };
      if ((!touchDevice && e.type === 'mouseenter') || e.type === 'click') {
        const interactionType = e.type === 'mouseenter' ? 'HOVER' : 'CLICK';
        track(() => [
          evSwatchInteract,
          {
            mainStyleId: styleId,
            interactedProduct: swatch,
            interactedSwatchIndex,
            interactionType
          }
        ]);
      }
      setCurrentProduct(newProduct);
    }, 500);

  const makeProductMediaHoverHandler = () => {
    onProductMediaHovered(currentProduct, styleId);
  };

  return (
    <ProductCard
      {...currentProduct}
      productUrl={originalQueryString ? appendQuery(currentProduct.productUrl, originalQueryString.toString()) : currentProduct.productUrl}
      imageClassName={mainProduct.rowHasBadge && css.rowHasBadge}
      onMediaHovered={makeProductMediaHoverHandler}
      data-test-id={testId}
      hearts={heartsInfo}
      CardDetailsTopSlot={enableColorSwatches ? ColorSwatches : undefined}
      makeSwatchClickHandler={makeSwatchClickHandler}
      animationTimerIndex={animationTimerIndex}
      relativeBadgeClassName={relativeBadgeClassName}
      absoluteBadgeClassName={absoluteBadgeClassName}
      className={className}
      eventLabel={eventLabel}
      isCrossSiteSearch={isCrossSiteSearch}
      siteName={siteName}
      onComponentClick={onComponentClick}
      onClick={onClick}
      allowSwatchGrouping={allowSwatchGrouping}
    />
  );
};

export default ColorSwatchWrapper;
